import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetReviewQuery, useUpdateReviewStatusMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import { showSuccess, showError } from '../../../utils/toast';
import { confirmAction } from '../../../utils/confirm';

const ReviewDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useGetReviewQuery(id!);
  const [updateReviewStatus] = useUpdateReviewStatusMutation();

  const review = data?.data;

  const handleStatusUpdate = async (newStatus: string) => {
    const confirmed = await confirmAction(
      'Update Status',
      `Are you sure you want to mark this review as ${newStatus}?`,
      `Yes, ${newStatus}`
    );
    
    if (confirmed) {
      try {
        const result = await updateReviewStatus({ id: Number(id), status: newStatus }).unwrap();
        showSuccess(result.message || 'Review status updated successfully!');
        refetch();
      } catch (err: any) {
        showError(err?.data?.message || 'Failed to update review status. Please try again.');
      }
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'Active': return 'badge-light-success';
      case 'Rejected': return 'badge-light-danger';
      case 'Pending': return 'badge-light-warning';
      default: return 'badge-light-primary';
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (error || !review) {
    return (
      <div className="alert alert-danger">
        {error ? 'Error loading review details' : 'Review not found'}
      </div>
    );
  }

  return (
    <div className="d-flex flex-column flex-column-fluid">
      <div className="app-toolbar py-3 py-lg-6">
        <div className="app-container container-xxl d-flex flex-stack">
          <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
            <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Review Details</h1>
            <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
              <li className="breadcrumb-item text-muted">
                <span className="text-muted text-hover-primary" onClick={() => navigate('/admin/dashboard')} style={{ cursor: 'pointer' }}>Home</span>
              </li>
              <li className="breadcrumb-item">
                <span className="bullet bg-gray-400 w-5px h-2px"></span>
              </li>
              <li className="breadcrumb-item text-muted">
                <span className="text-muted text-hover-primary" onClick={() => navigate('/admin/reviews')} style={{ cursor: 'pointer' }}>Reviews</span>
              </li>
              <li className="breadcrumb-item">
                <span className="bullet bg-gray-400 w-5px h-2px"></span>
              </li>
              <li className="breadcrumb-item text-dark">Details</li>
            </ul>
          </div>
          <div className="d-flex align-items-center gap-2 gap-lg-3">
            <button className="btn btn-sm btn-light" onClick={() => navigate('/admin/reviews')}>
              Back to List
            </button>
          </div>
        </div>
      </div>

      <div className="app-content flex-column-fluid">
        <div className="app-container container-xxl">
          <div className="row g-5 g-xl-10">
            <div className="col-xl-4">
              <div className="card mb-5 mb-xl-8">
                <div className="card-body pt-15">
                  <div className="d-flex flex-center flex-column mb-5">
                    <div className="symbol symbol-100px symbol-circle mb-7">
                      <img src={review.user?.picture || '/media/avatars/blank.png'} alt={review.user?.name} />
                    </div>
                    <span className="fs-3 text-gray-800 text-hover-primary fw-bold mb-1">{review.user?.name}</span>
                    <div className="fs-5 fw-semibold text-muted mb-6">{review.user?.email}</div>
                  </div>
                  <div className="d-flex flex-stack fs-4 py-3">
                    <div className="fw-bold rotate collapsible" data-bs-toggle="collapse" data-bs-target="#kt_user_view_details" role="button" aria-expanded="true" aria-controls="kt_user_view_details">Details
                      <span className="ms-2 rotate-180">
                        <i className="fas fa-chevron-down fs-7"></i>
                      </span>
                    </div>
                  </div>
                  <div className="separator separator-dashed my-3"></div>
                  <div id="kt_user_view_details" className="collapse show">
                    <div className="pb-5 fs-6">
                      <div className="fw-bold mt-5">User ID</div>
                      <div className="text-gray-600">{review.user_id}</div>
                      <div className="fw-bold mt-5">Phone</div>
                      <div className="text-gray-600">{review.user?.phone || 'N/A'}</div>
                      <div className="fw-bold mt-5">Status</div>
                      <div className="text-gray-600">
                        <span className={`badge ${getStatusBadgeClass(review.status)}`}>{review.status}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-8">
              <div className="card mb-5 mb-xl-10">
                <div className="card-header border-0 cursor-pointer" role="button" data-bs-toggle="collapse" data-bs-target="#kt_account_profile_details" aria-expanded="true" aria-controls="kt_account_profile_details">
                  <div className="card-title m-0">
                    <h3 className="fw-bold m-0">Review Content</h3>
                  </div>
                </div>
                <div id="kt_account_profile_details" className="collapse show">
                  <div className="card-body border-top p-9">
                    <div className="row mb-7">
                      <label className="col-lg-4 fw-semibold text-muted">Ad ID</label>
                      <div className="col-lg-8">
                        <span className="fw-bold fs-6 text-gray-800">{review.ad?.ad_id || 'N/A'}</span>
                      </div>
                    </div>
                    <div className="row mb-7">
                      <label className="col-lg-4 fw-semibold text-muted">Ad Title</label>
                      <div className="col-lg-8">
                        <span className="fw-bold fs-6 text-gray-800">{review.ad?.title}</span>
                      </div>
                    </div>
                    <div className="row mb-7">
                      <label className="col-lg-4 fw-semibold text-muted">Stars</label>
                      <div className="col-lg-8">
                        <div className="rating">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <div key={star} className={`rating-label ${star <= review.stars ? 'checked' : ''}`}>
                              <i className="fas fa-star fs-4"></i>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="row mb-7">
                      <label className="col-lg-4 fw-semibold text-muted">Comment</label>
                      <div className="col-lg-8">
                        <span className="fw-bold fs-6 text-gray-800">{review.comment || 'No comment provided'}</span>
                      </div>
                    </div>
                    <div className="row mb-7">
                      <label className="col-lg-4 fw-semibold text-muted">Posted On</label>
                      <div className="col-lg-8">
                        <span className="fw-bold fs-6 text-gray-800">{new Date(review.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="card-footer d-flex justify-content-end py-6 px-9 gap-3">
                    {review.status !== 'Active' && (
                      <button 
                        type="button" 
                        className="btn btn-success"
                        onClick={() => handleStatusUpdate('Active')}
                      >
                        Approve Review
                      </button>
                    )}
                    {review.status !== 'Rejected' && (
                      <button 
                        type="button" 
                        className="btn btn-danger"
                        onClick={() => handleStatusUpdate('Rejected')}
                      >
                        Reject Review
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewDetails;
